import React from "react";
import { siteConfig, contactInfo, externalLinks, logoUrl } from "../data/mock";
import { ArrowUpRight, Mail, MapPin, ExternalLink, Instagram, ShieldCheck, FileCheck, Building2, Gavel, Radio } from "lucide-react";
import { Separator } from "./ui/separator";

const Footer = () => {
  return (
    <footer className="bg-[rgb(26,28,30)] border-t border-[rgba(255,255,255,0.1)]">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 py-12 sm:py-16 md:py-24">
        {/* Trust Statement */}
        <div className="mb-10 sm:mb-16">
          <p
            className="text-[rgb(218,255,1)] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] font-medium mb-3 sm:mb-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            GLOBAL SYNC CLEARANCE
          </p>
          <h3
            className="text-white text-xl sm:text-2xl md:text-4xl font-bold leading-tight max-w-xl"
            style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: "0.02em" }}
          >
            {siteConfig.trustStatement}
          </h3>
        </div>

        <Separator className="bg-[rgb(63,63,63)] mb-8 sm:mb-12" />

        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 justify-between">
          {/* Brand & Corporate Trust */}
          <div>
            <div className="flex items-center gap-2.5 mb-4 sm:mb-5">
              <img
                src={logoUrl}
                alt="ItzGreenMusic"
                className="h-7 sm:h-8 w-auto object-contain rounded"
              />
              <span
                className="text-white text-xs sm:text-sm tracking-[0.25em] sm:tracking-[0.3em] font-semibold"
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {siteConfig.name}
              </span>
            </div>
            <p
              className="text-[rgb(161,161,170)] text-xs sm:text-sm leading-relaxed max-w-xs mb-5 sm:mb-6"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Boutique sync catalog providing one-stop licensing solutions for
              film, television, and streaming platforms worldwide.
            </p>
            
            {/* Corporate Credentials */}
            <div className="space-y-2 mb-5 sm:mb-6">
               <div className="flex items-center gap-2 text-[rgb(161,161,170)] text-[10px] sm:text-xs font-mono">
                  <ShieldCheck size={12} className="text-[rgb(218,255,1)] flex-shrink-0 sm:hidden" />
                  <ShieldCheck size={14} className="text-[rgb(218,255,1)] flex-shrink-0 hidden sm:block" />
                  <span>PRO: BMI | Publisher IPI: 01323361389</span>
               </div>
               <div className="flex items-center gap-2 text-[rgb(161,161,170)] text-[10px] sm:text-xs font-mono">
                  <FileCheck size={12} className="text-[rgb(218,255,1)] flex-shrink-0 sm:hidden" />
                  <FileCheck size={14} className="text-[rgb(218,255,1)] flex-shrink-0 hidden sm:block" />
                  <span>Mechanicals: Approved Member of The MLC</span>
               </div>
               <div className="flex items-center gap-2 text-[rgb(161,161,170)] text-[10px] sm:text-xs font-mono">
                  <Radio size={12} className="text-[rgb(218,255,1)] flex-shrink-0 sm:hidden" />
                  <Radio size={14} className="text-[rgb(218,255,1)] flex-shrink-0 hidden sm:block" />
                  <span>SoundExchange Rights Owner (Payee: 2181132146)</span>
               </div>
               <div className="flex items-center gap-2 text-[rgb(161,161,170)] text-[10px] sm:text-xs font-mono">
                  <Building2 size={12} className="text-[rgb(218,255,1)] flex-shrink-0 sm:hidden" />
                  <Building2 size={14} className="text-[rgb(218,255,1)] flex-shrink-0 hidden sm:block" />
                  <span>HQ: Hoboken, NJ</span>
               </div>
            </div>

            {/* Legal Shield */}
            <div className="bg-[rgba(255,255,255,0.03)] p-3 sm:p-4 rounded-lg border border-[rgba(255,255,255,0.05)]">
                <div className="flex items-center gap-2 mb-2 text-[rgb(218,255,1)]">
                    <Gavel size={12} className="sm:hidden" />
                    <Gavel size={14} className="hidden sm:block" />
                    <span className="text-[9px] sm:text-[10px] tracking-widest font-bold font-['Inter']">LEGAL NOTICE</span>
                </div>
                <p className="text-[rgb(100,100,110)] text-[9px] sm:text-[10px] leading-relaxed font-['Inter']">
                    ItzGreenMusic operates a strict zero-tolerance policy for generative AI and uncleared samples. By submitting audio to this portal, the sender legally warrants that all materials are 100% human-composed and free of copyright infringement.
                </p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <p
              className="text-[rgb(161,161,170)] text-[10px] sm:text-xs tracking-[0.2em] mb-4 sm:mb-5"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              CONTACT
            </p>
            <div className="space-y-3 sm:space-y-4">
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-center gap-2 sm:gap-3 text-white text-xs sm:text-sm hover:text-[rgb(218,255,1)] transition-colors group"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <Mail size={14} className="text-[rgb(161,161,170)] group-hover:text-[rgb(218,255,1)] transition-colors sm:hidden" />
                <Mail size={16} className="text-[rgb(161,161,170)] group-hover:text-[rgb(218,255,1)] transition-colors hidden sm:block" />
                {contactInfo.email}
              </a>
              <div className="flex items-center gap-2 sm:gap-3 text-[rgb(161,161,170)] text-xs sm:text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                <MapPin size={14} className="sm:hidden" />
                <MapPin size={16} className="hidden sm:block" />
                {contactInfo.location}
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-[rgb(63,63,63)] mt-8 sm:mt-12 mb-6 sm:mb-8" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 sm:gap-4">
          <p
            className="text-[rgb(161,161,170)] text-[10px] sm:text-xs"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {siteConfig.copyright}
          </p>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 sm:gap-4 md:gap-8">
            <a
              href={externalLinks.artistSubmission}
              className="text-[rgb(161,161,170)] text-[10px] sm:text-xs hover:text-[rgb(218,255,1)] transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Catalog Submissions
            </a>
            <p
              className="text-[rgb(161,161,170)] text-[10px] sm:text-xs"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              All music pre-cleared for global sync licensing.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
